import Button from '@atlaskit/button';
import { ErrorMessage, Field, Fieldset } from '@atlaskit/form';
import AddIcon from '@atlaskit/icon/glyph/add';
import EditorSuccessIcon from '@atlaskit/icon/glyph/editor/success';
import Spinner from '@atlaskit/spinner';
import Textfield from '@atlaskit/textfield';
import { G500 } from '@atlaskit/theme/colors';
import { createAtlassianAccountWithNameAndEmail } from '@atlassian/temp-proxies';
import cloneDeep from 'lodash/cloneDeep';
import debounce from 'lodash/debounce';
import React, { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import isEmail from 'validator/lib/isEmail';
import './add-contact-fields.scss';
import { validateEmailByAtlassianAccount } from './helper';
import { SignUpBanner } from './sign-up-banner';
import { SuccessBanner } from './success-banner';

export interface AddContactProps {
  atlassianAccountIds: Array<string>;
  setAtlassianAccountIds: Dispatch<SetStateAction<Array<string>>>;
  atlassianAccountEmails: Array<string>;
  setAtlassianAccountEmails: Dispatch<SetStateAction<Array<string>>>;
  emailFieldText: string;
  nameFieldText: string;
  addButtonText: string;
  validateEmailField?: (_email: string) => string | undefined;
}

enum AtlassianAccountStatus {
  FETCHED = 'FETCHED',
  FETCHING = 'FETCHING',
  NOT_FETCHED = 'NOT_FETCHED',
  CREATED = 'CREATED',
}

const DEFAULT_CONTACT_OBJECT = {
  email: '',
  name: '',
  atlassianAccountStatus: AtlassianAccountStatus.NOT_FETCHED,
  error: '',
  isEditable: true,
  nameError: false,
};

interface Contact {
  email: string;
  name: string;
  atlassianAccountStatus: AtlassianAccountStatus;
  error: string;
  isEditable: boolean;
  nameError: boolean;
}

export const AddContactFields = ({
  atlassianAccountIds,
  setAtlassianAccountIds,
  atlassianAccountEmails,
  setAtlassianAccountEmails,
  emailFieldText,
  nameFieldText,
  addButtonText,
  validateEmailField,
}: AddContactProps) => {
  const [userList, setUserList] = useState<Array<Contact>>([{ ...DEFAULT_CONTACT_OBJECT }]);

  const validateEmail = useCallback(
    (email: string | undefined) => {
      if (!email) return 'Email address is required';
      if (!isEmail(email)) return 'Please enter a valid email';
      if (validateEmailField) return validateEmailField(email);
    },
    [validateEmailField]
  );

  const addNewContact = useCallback(() => {
    const clonedUserList = cloneDeep(userList);
    clonedUserList[clonedUserList.length - 1] = { ...clonedUserList[clonedUserList.length - 1], isEditable: false };
    setUserList([...clonedUserList, { ...DEFAULT_CONTACT_OBJECT }]);
    setAtlassianAccountIds([...atlassianAccountIds, '']);
    setAtlassianAccountEmails([...atlassianAccountEmails, '']);
  }, [atlassianAccountEmails, atlassianAccountIds, setAtlassianAccountEmails, setAtlassianAccountIds, userList]);

  const removeCustomerContact = useCallback(
    (idx) => {
      setUserList([...userList.slice(0, idx), ...userList.slice(idx + 1)]);
      setAtlassianAccountIds([...atlassianAccountIds.slice(0, idx), ...atlassianAccountIds.slice(idx + 1)]);
      setAtlassianAccountEmails([...atlassianAccountEmails.slice(0, idx), ...atlassianAccountEmails.slice(idx + 1)]);
    },
    [atlassianAccountEmails, atlassianAccountIds, setAtlassianAccountEmails, setAtlassianAccountIds, userList]
  );

  const validateEmailByAtlassianAccountCallback = useCallback(
    async (email: string, newUserList: Array<Contact>, idx: number) => {
      if (!validateEmail(email)) {
        const res = await validateEmailByAtlassianAccount(email);
        const clonedUserList = cloneDeep(newUserList);
        clonedUserList[idx].atlassianAccountStatus = AtlassianAccountStatus.FETCHED;
        clonedUserList[idx].error = res.error;
        setAtlassianAccountIds((prevValue) => {
          const clonedValue = cloneDeep(prevValue);
          clonedValue[idx] = res.id;
          return clonedValue;
        });
        setAtlassianAccountEmails((prevValue) => {
          const clonedValue = cloneDeep(prevValue);
          clonedValue[idx] = email;
          return clonedValue;
        });
        setUserList(clonedUserList);
      }
    },
    [setAtlassianAccountEmails, setAtlassianAccountIds, validateEmail]
  );

  const debouncedValidateEmailByAtlassian = useMemo(() => debounce(validateEmailByAtlassianAccountCallback, 1000), [
    validateEmailByAtlassianAccountCallback,
  ]);

  const handleEmailUpdate = useCallback(
    (email: string, idx: number) => {
      const newUserList = cloneDeep(userList);
      newUserList[idx].email = email;
      newUserList[idx].name = '';
      newUserList[idx].error = '';
      newUserList[idx].atlassianAccountStatus = AtlassianAccountStatus.NOT_FETCHED;
      if (!validateEmail(email)) {
        newUserList[idx].atlassianAccountStatus = AtlassianAccountStatus.FETCHING;
      }
      debouncedValidateEmailByAtlassian(email, newUserList, idx);
      setUserList(newUserList);
      setAtlassianAccountIds((prevValue) => {
        const clonedValue = cloneDeep(prevValue);
        clonedValue[idx] = '';
        return clonedValue;
      });
      setAtlassianAccountEmails((prevValue) => {
        const clonedValue = cloneDeep(prevValue);
        clonedValue[idx] = '';
        return clonedValue;
      });
    },
    [debouncedValidateEmailByAtlassian, setAtlassianAccountEmails, setAtlassianAccountIds, userList, validateEmail]
  );

  const createAtlassianAccount = useCallback(
    async (idx: number) => {
      const newUserList = cloneDeep(userList);
      try {
        if (userList[idx].name) {
          const res = await createAtlassianAccountWithNameAndEmail(userList[idx].email, userList[idx].name, 'wac');
          newUserList[idx].atlassianAccountStatus = AtlassianAccountStatus.CREATED;
          const clonedAtlassianAccountIds = cloneDeep(atlassianAccountIds);
          clonedAtlassianAccountIds[idx] = res.id;
          setAtlassianAccountIds(clonedAtlassianAccountIds);
          const clonedAtlassianAccountEmails = cloneDeep(atlassianAccountEmails);
          clonedAtlassianAccountEmails[idx] = userList[idx].email;
          setAtlassianAccountEmails(clonedAtlassianAccountEmails);
        } else {
          newUserList[idx].nameError = true;
        }
      } catch (error) {
        newUserList[idx].error = `Error in creating an Atlassian Account for ${userList[idx].email}`;
      }
      setUserList(newUserList);
    },
    [atlassianAccountEmails, atlassianAccountIds, setAtlassianAccountEmails, setAtlassianAccountIds, userList]
  );

  const getAccountSignUpBanner = useCallback(
    (idx: number) => {
      if (
        !atlassianAccountIds[idx] &&
        userList[idx].atlassianAccountStatus === AtlassianAccountStatus.FETCHED &&
        isEmail(userList[idx].email) &&
        !userList[idx].error
      ) {
        return (
          <SignUpBanner email={userList[idx].email} handleCreateAtlassianAccount={() => createAtlassianAccount(idx)} />
        );
      }
    },
    [atlassianAccountIds, createAtlassianAccount, userList]
  );

  const getNameField = useCallback(
    (idx: number) => {
      if (
        !atlassianAccountIds[idx] &&
        userList[idx].atlassianAccountStatus === AtlassianAccountStatus.FETCHED &&
        isEmail(userList[idx].email) &&
        !userList[idx].error
      ) {
        return (
          <Field name={`recipients-name[${idx}]`} isRequired defaultValue={userList[idx].name}>
            {({ fieldProps }) => (
              <>
                <Textfield
                  {...fieldProps}
                  testId={`customer-contact-name-${idx}`}
                  autoComplete="off"
                  placeholder={nameFieldText}
                  onChange={(event: React.FormEvent<HTMLInputElement>) => {
                    const newUserList = cloneDeep(userList);
                    newUserList[idx].name = event.currentTarget.value;
                    newUserList[idx].nameError = !event.currentTarget.value;
                    setUserList(newUserList);
                    fieldProps.onChange(event);
                  }}
                />
                {userList[idx].nameError && <ErrorMessage>{'Full name is required'}</ErrorMessage>}
              </>
            )}
          </Field>
        );
      }
    },
    [atlassianAccountIds, nameFieldText, userList]
  );

  const getCreateAtlassianAccountSuccessBanner = useCallback(
    (idx: number) => {
      if (userList[idx].atlassianAccountStatus === AtlassianAccountStatus.CREATED) {
        return <SuccessBanner email={userList[idx].email} />;
      }
    },
    [userList]
  );

  const getElementAfterEmailInput = useCallback(
    (idx) => {
      if (userList[idx].atlassianAccountStatus === AtlassianAccountStatus.FETCHING) {
        return (
          <div className="input-after-icon">
            <Spinner size="small" />
          </div>
        );
      } else if (atlassianAccountIds[idx]) {
        return (
          <div className="input-after-icon">
            <EditorSuccessIcon size="medium" primaryColor={G500} label="" />
          </div>
        );
      }
      return null;
    },
    [atlassianAccountIds, userList]
  );

  const getRemoveContactButton = useCallback(
    (idx) => {
      return (
        <Button
          appearance="link"
          testId={`remove-button-${idx}`}
          onClick={() => removeCustomerContact(idx)}
          className="remove-button"
        >
          Remove
        </Button>
      );
    },
    [removeCustomerContact]
  );

  return (
    <div className="add-contact-container">
      {userList.map((user, idx) => {
        if (user.isEditable) {
          return (
            <Fieldset key={idx}>
              <div className="contact-field">
                <div>
                  <Field
                    name={`recipients-email[${idx}]`}
                    defaultValue={user.email}
                    isRequired
                    validate={(email) => validateEmail(email)}
                  >
                    {({ fieldProps, error }) => {
                      return (
                        <>
                          <div className="add-contact">
                            <Textfield
                              {...fieldProps}
                              testId={`customer-contact-email-${idx}`}
                              autoComplete="off"
                              placeholder={emailFieldText}
                              onChange={(event: React.FormEvent<HTMLInputElement>) => {
                                handleEmailUpdate(event.currentTarget.value, idx);
                                fieldProps.onChange(event);
                              }}
                              elemAfterInput={getElementAfterEmailInput(idx)}
                            />
                          </div>
                          {error && <ErrorMessage>{error}</ErrorMessage>}
                        </>
                      );
                    }}
                  </Field>
                  {getNameField(idx)}
                  {getAccountSignUpBanner(idx)}
                  {getCreateAtlassianAccountSuccessBanner(idx)}
                  {user.error && <ErrorMessage testId={`error-in-fetching-${idx}`}>{user.error}</ErrorMessage>}
                </div>
                {idx > 0 && getRemoveContactButton(idx)}
              </div>
            </Fieldset>
          );
        } else {
          return (
            <div key={idx} className="user-info-section">
              <div className="user-email">{user.email}</div>
              {atlassianAccountIds.length > 1 && getRemoveContactButton(idx)}
            </div>
          );
        }
      })}

      <div className="btn-add-another-email-address">
        <Button
          appearance="link"
          testId="add-new-customer-contact"
          iconBefore={<AddIcon label="Add icon" size="small" />}
          onClick={addNewContact}
          isDisabled={!atlassianAccountIds[atlassianAccountIds.length - 1]}
        >
          {addButtonText}
        </Button>
      </div>
    </div>
  );
};
